import { message, Modal, Row, Button, Col, Select, Radio, Popconfirm } from 'antd';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Dispatch } from 'redux';
import I18N from 'src/services/I18NService';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import { IAreaInfo } from 'src/entity/LocationEntity';
import LocationService from 'src/services/LocationService';
import DoctorService from 'src/services/DoctorService';
import { IDoctorSearch } from 'src/entity/SearchEntity';
import { IDoctorInfo, IBatchModifySupplierParam, ISupplierDict, IBatchSupplierValidateRes } from 'src/entity/DoctorEntity';
import { IStateCollection } from 'src/entity/StateEntity';
import { isArray } from 'util';
import Table, { ColumnProps } from 'antd/lib/table';
import { UtilDateTime } from 'src/utils/datetime';
import Form, { FormComponentProps } from 'antd/lib/form';
import { PaginationProps } from 'antd/lib/pagination';
import { IMallPricePolicy, IMallPharmacyDetail } from 'src/entity/MallEntity';
import { fetchDrugStoresIfNeeded } from 'src/redux/actions/drugStoreCreator';
import { IAgentInfo } from 'src/entity/AgentEntity';
import { fetchAllPharmacyList, fetchPharmacyListBySellerUser, fetchPricePolicyByPharmacyUUID } from 'src/redux/actions/mallPharmacyCreator';
import { RadioChangeEvent } from 'antd/lib/radio';
import SupplierPharmacySelect from 'src/components/common/SupplierPharmacySelect';

interface IDoctorBatchModifyFormProps extends FormComponentProps, RouteComponentProps {
  /** 代理商关联的配药中心列表 */
  drugStoreList: IDrugStoreInfo[];
  userInfo: IAgentInfo | IDrugStoreInfo | undefined;
  isAgent: boolean;
  dispatch: Dispatch<IStateCollection>;
} 

interface IDoctorBatchModifyFormState {
  /** 查询类型 */
  searchType: 'area' | 'all';
  /** 省份列表 */
  provinceList: IAreaInfo[];
  /** 城市列表 */
  cityList: IAreaInfo[];
  /** 区县列表 */
  districtList: IAreaInfo[];
  /** 查询条件中选中的配药中心uuid */
  selectedPharmacy?: string;
  /** 选中的省 */
  selectedProvince?: IAreaInfo,
  /** 选中的城市 */
  selectedCity?: IAreaInfo,
  /** 选中的区 */
  selectedDistrict?: IAreaInfo,
  /** 是否正在查询中 */
  searchLoading: boolean,
  /** 验证接口是否正在调用 */
  validateLoading: boolean,
  /** 是否正在提交  */
  submitLoading: boolean,
  /** 是否显示模态窗 */
  modalVisible: boolean,
  /** 医生列表 */
  doctorList: IDoctorInfo[],
  /** table列表项配置 */
  columns: Array<ColumnProps<IDoctorInfo>>;
  /** 单元药库列表 */
  unitPharmacyList: IMallPharmacyDetail[];
  /** 代理商以及关联配药中心的所有单元药库列表 */
  allPharmacyList: IMallPharmacyDetail[];
  /** 新增关联: 代理商以及关联配药中心的所有价格模式 */
  priceModeList: IMallPricePolicy[];
  /** 供应商列表(包含本代理商和关联配药中心) */
  supplierList: ISupplierDict[];
  /** 错误提示列表 */
  errorList: string[];
  /** 分页设置 */
  pagination: PaginationProps;
}



/**
 * 代理商医生批量修改供应商
 * @class DoctorListPage
 * @extends {React.Component<IDoctorBatchModifyFormProps, IDoctorBatchModifyFormState>}
 */
class DoctorBatchModifyForm extends React.Component<IDoctorBatchModifyFormProps, IDoctorBatchModifyFormState> {
  private tempSubmitParam: {
    operateType: 'bind' | 'unBind',
    param: IBatchModifySupplierParam,
    selectedSupplier: ISupplierDict | undefined
  } | undefined;
  constructor(props: IDoctorBatchModifyFormProps) {
    super(props);
    // 表格列配置
    const columns: Array<ColumnProps<IDoctorInfo>> = [
      {
        title: `${I18N.get('common_user')}ID`,
        key: 'id',
        dataIndex: 'id',
        align: 'center',
      },
      {
        title: `${I18N.get('common_user')}${I18N.get('common_name')}`,
        key: 'first_name',
        dataIndex: 'first_name',
        align: 'center',
        render: (text: string, record: IDoctorInfo) => `${record.last_name}${record.first_name}`,
      },
      {
        title: `${I18N.get('common_register_time')}`,
        key: 'created_time',
        dataIndex: 'created_time',
        align: 'center',
        render: (text: string) => UtilDateTime.datetimeFormat('yyyy-MM-dd', new Date(text)),
      },
      {
        title: `${I18N.get('doc_area_belong')}`,
        key: 'location_fullname',
        dataIndex: 'location_fullname',
        align: 'center',
        render: (text: string[]) => {
          let result = '';
          if (isArray(text)) {
            result = text.join('');
          }
          return result;
        }
      },
    ];
    this.state = {
      searchType: 'area',
      provinceList: [],
      cityList: [],
      districtList: [],
      selectedProvince: undefined,
      selectedCity: undefined,
      selectedDistrict: undefined,
      searchLoading: false,
      validateLoading: false,
      submitLoading: false,
      modalVisible: false,
      doctorList: [],
      columns,
      priceModeList: [],
      supplierList: [],
      unitPharmacyList: [],
      allPharmacyList: [],
      errorList: [],
      pagination: {},
    };
  }

  async componentDidMount() {
    await this.props.dispatch(fetchDrugStoresIfNeeded());
    this.getSupplierList();
    this.initArea();
    this.getAllPharmacyList();
  }

  componentWillUnmount() {
    this.setState = () => {
      return;
    };
  }

  /**
   * 获取代理商以及关联配药中心的所有单元药库
   */
  async getAllPharmacyList(): Promise<IMallPharmacyDetail[]> {
    let result: IMallPharmacyDetail[] = [];
    const { allPharmacyList } = this.state;
    if (!allPharmacyList.length) {
      if (!!this.props.userInfo) {
        const sellerUserUUIDList = [this.props.userInfo.seller_user].concat(this.props.drugStoreList.map(t => t.seller_user));
        result = await fetchAllPharmacyList(sellerUserUUIDList);
        this.setState({allPharmacyList: result})
      }
    } else {
      result = allPharmacyList;
    }
    return result;
  }

  /**
   * 追加医生去重
   * [sourceList | newList] 自身无重复项
   */
  private filterDoctors(sourceList: IDoctorInfo[], newList: IDoctorInfo[]): IDoctorInfo[] {
    const result: IDoctorInfo[] = [];
    newList.forEach(newItem => {
      const needAdd = sourceList.findIndex(sourceItem => sourceItem.id === newItem.id) === -1;
      if (needAdd) {
        result.push(newItem);
      }
    });
    return result;
  }

  /**
   * @description 过滤出所有生效单元药库 
   */
  private optionalUnitPharmacy(unitPharmacy: IMallPharmacyDetail[]): IMallPharmacyDetail[] {
    return unitPharmacy.filter((item: IMallPharmacyDetail) => {
      return !!item.is_active && !item.is_deleted;
    })
  }

  /**
   * 初始化地区服务
   */
  private async initArea() {
    try {
      await LocationService.initArea();
      const provinceList: IAreaInfo[] = LocationService.getLinkageProvince();
      this.setState({
        provinceList,
      });
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * 获取医生虚拟机构uuid数组
   */
  private getDoctorUUIDs(doctorList: IDoctorInfo[]): string[] {
    return doctorList.map(item => item.organization);
  }


  private transferErrorList(errorList: string[], medicineGroup: string): string[] {
    // 有错误信息，弹出提示框
    const result: string[] = [];
    errorList.forEach(item => {
      switch(item) {
        case 'has_online_order_doctors':
          // 新增关联代理商的单元药库时, 有部分医生开通了在线订购报错提示
          result.push(I18N.get('doc_batch_modify_online_error'));
          break;
        case 'has_no_online_order_doctors':
          // 新增关联配药中心的单元药库时, 有部分医生未开通在线订购报错提示
          result.push(I18N.get('doc_batch_modify_noOnline_error'));
          break;
        case 'has_not_in_regions_doctors':
           // 新增关联配药中心的单元药库时, 有部分医生执业地址不在配药中心服务区域内
          result.push(I18N.get('doc_batch_modify_region_error'));
          break;
        case 'last_doctor_drug_store_warning':
          // 取消关联单元药库时，此药库是唯一单元药库
          if (!!medicineGroup) {
            const matchedPharmacy = this.state.allPharmacyList.find(pharmacy => pharmacy.uuid === medicineGroup);
            if (!!matchedPharmacy) {
              if (matchedPharmacy.seller_user === (this.props.userInfo as IAgentInfo).seller_user) {
                // 绑定代理商单元药库错误提示
                result.push(I18N.get('doc_batch_modify_relate_agent_error'));
              } else {
                // 绑定配药中心单元药库错误提示
                result.push(I18N.get('doc_batch_modify_relate_drugstore_error'));
              }
            }
          }
          break;
      }
    })
    return result;
  }

  /**
   * 返回上一页
   */
  goback = () => {
    this.props.history.goBack();
  };

  /**
   * 处理查询类型变化事件
   */
  handleSearchTypeChange = async (e: RadioChangeEvent) => {
    const searchType  = e.target.value as 'area' | 'all';
    this.setState({searchType});
    // 清空查询条件
    this.setState({
      selectedProvince: undefined,
      selectedCity: undefined,
      selectedDistrict: undefined,
      cityList: [],
      districtList: [],
      selectedPharmacy: undefined,
    })
    // 清空已选择的医生
    this.clearDoctorList();
    if (searchType === 'all') {
      this.setState({searchLoading: true});
      // 查询全部医生
      try {
        const param: IDoctorSearch = {
          check: true,
        };
        const res = await DoctorService.getAllAgentRelateDoctors(param);
        if (!!res && Array.isArray(res.results)) {
          this.setState({doctorList: res.results, pagination: { current: 1, total: res.results.length}});
        }
      } catch (error) {
        console.error(error);
        message.error(`${I18N.get('err_search_failure')}`);
      }
      this.setState({searchLoading: false});
    }
  }

  onUnitPharmacySelect = (value: string) => {
    this.setState({selectedPharmacy: value});
  }

  /**
   * 省份变化
   */
  provinceChange = (value: string) => {
    let selectedProvince: IAreaInfo | undefined;
    let cityList: IAreaInfo[] = [];
    if (!!value) {
      selectedProvince = LocationService.areaMap.get(Number(value)) as IAreaInfo;
      cityList = LocationService.getLinkageAreaByParentId(selectedProvince.value);
    }
    this.setState({
      cityList,
      districtList: [],
      selectedProvince,
      selectedCity: undefined,
      selectedDistrict: undefined,
    });
  }

  /**
   * 城市变化
   */
  cityChange = (value: string) => {
    let selectedCity: IAreaInfo | undefined;
    let districtList: IAreaInfo[] = [];
    if (!!value) {
      selectedCity = LocationService.areaMap.get(Number(value)) as IAreaInfo;
      districtList = LocationService.getLinkageAreaByParentId(selectedCity.value);
    }
    this.setState({
      districtList,
      selectedCity,
      selectedDistrict: undefined,
    });
  }

  /**
   * 乡县区变化
   */
  districtChange = (value: string) => {
    let selectedDistrict: IAreaInfo | undefined;
    if (!!value) {
      selectedDistrict = LocationService.areaMap.get(Number(value)) as IAreaInfo;
    }
    this.setState({
      selectedDistrict,
    });
  }

  /**
   * 查询并添加医生
   */
  searchDoctors = async () => {
    const { selectedProvince, selectedCity, selectedDistrict, doctorList, selectedPharmacy} = this.state;
    const areaNoSelected = !selectedProvince && !selectedCity && !selectedDistrict;
    const pharmacyNoSelected = !selectedPharmacy;
    if (areaNoSelected && pharmacyNoSelected) {
      // 查询条件为空
      message.error(`${I18N.get('common_choose')}${I18N.get('doc_area_belong')}${I18N.get('common_or')}${I18N.get('unit_pharmacy')}`);
      return;
    }
    const param: IDoctorSearch = {
      medicine_group: !!selectedPharmacy ? selectedPharmacy : undefined,
      province: !!selectedProvince ? selectedProvince.value : undefined,
      city: !!selectedCity ? selectedCity.value : undefined,
      area: !!selectedDistrict ? selectedDistrict.value : undefined,
      check: true,
    };
    this.setState({searchLoading: true});
    try {
      const res = await DoctorService.getAllAgentRelateDoctors(param);
      if (!!res && isArray(res.results)) {
        const needAddDoctors = this.filterDoctors(doctorList, res.results);
        const resultList = [...needAddDoctors, ...doctorList];
        this.setState({doctorList: resultList, pagination: { current: 1, total: resultList.length}});
      }
    } catch (error) {
      console.error(error);
      message.error(`${I18N.get('err_search_failure')}`);
    }
    this.setState({searchLoading: false});
  }

  /**
   * 清空选择的医生列表
   */
  clearDoctorList = () => {
    this.setState({doctorList: []});
  }

  /**
   * TODO: Radio.Button onchange触发两次，ant版本太低
   * 处理[绑定|解除]关联操作变化
   * 1, 单元药库当前选中值 => 清空
   * 2, 单元药库的下拉选项动态变化:
   *    新增关联 => 选项清空
   *    解除关联 => 选项设为该代理商全部可见(包含关联配药中心)的单元药库(包含生效以及未生效)
   */
  handleOperateChange = async (e: RadioChangeEvent) => {
    let unitPharmacyList: IMallPharmacyDetail[] = [];
    const operateType = e.target.value;
    if (operateType === 'unBind') {
      unitPharmacyList = await this.getAllPharmacyList();
    }
    this.props.form.setFieldsValue({'medicine_group': undefined});
    this.props.form.setFieldsValue({'medicine_price_policy': undefined});
    this.setState({unitPharmacyList});
  }

  /**
   * 配药中心改变时回调
   * 1, 单元药库: 已选中的值清空;下拉选项改为已选中的配药中心的单元药库列表
   * 2, 价格模式：已选中的值清空;下拉选项列表清空
   */
  onChangeDrugStore = async (sellerUserUUID: string) => {
    let unitPharmacyList: IMallPharmacyDetail[] = [];
    if (!!sellerUserUUID) {
      const pharmacyList = await fetchPharmacyListBySellerUser(sellerUserUUID);
      unitPharmacyList = this.optionalUnitPharmacy(pharmacyList);
    }
    this.props.form.setFieldsValue({'medicine_group': undefined});
    this.props.form.setFieldsValue({'medicine_price_policy': undefined});
    this.setState({
      priceModeList: [],
      unitPharmacyList,
    });
  };

  /**
   * 单元药库改变时回调
   * 价格模式：已选中的值清空;下拉选项列表改为已选中的单元药库的价格模式列表
   */
  onChangeUnitPharmacy = async (pharmacyUUID: string) => {
    this.props.form.setFieldsValue({'medicine_price_policy': undefined});
    const priceModeList = await fetchPricePolicyByPharmacyUUID(pharmacyUUID);
    this.setState({
      priceModeList,
    });
  }

  /**
   * 处理提交按钮点击事件
   * 调用后台验证接口
   */
  handleSubmit = () => {
    const {doctorList, validateLoading, submitLoading, searchType} = this.state;
    if (!doctorList.length) {
      message.error(`${I18N.get('doc_batch_modify_select_error')}`);
      return;
    }
    if (validateLoading || submitLoading) {
      return;
    }
    this.tempSubmitParam = undefined;
    this.props.form.validateFields(async (err, values) => {
      if (!err) {
        const doctorUUIDs = this.getDoctorUUIDs(doctorList);
        const operateType: 'bind' | 'unBind' = values.operate_type;
        const submitParam: IBatchModifySupplierParam = {
          doctors: doctorUUIDs,
          medicine_group: values.medicine_group,
          medicine_price_policy: values.medicine_price_policy,
        };
        let selectedSupplier: ISupplierDict | undefined;
        let errorRes: IBatchSupplierValidateRes | undefined;
        this.tempSubmitParam = {
          operateType,
          param: submitParam,
          selectedSupplier,
        };
        try {
          this.setState({validateLoading: true});
          if (operateType === 'unBind') {
            // 取消关联
            delete submitParam.medicine_price_policy;
            errorRes = await DoctorService.batchUnrelateSupplierValidator(submitParam);
          } else if (operateType === 'bind') {
            // 新增关联
            const {supplierList} = this.state;
            selectedSupplier = supplierList.find(supplier => supplier.seller_user === values.seller_user);
            if (!selectedSupplier || !selectedSupplier.seller_user ) {
              return;
            }
            errorRes = DoctorService.batchRelateSupplierValidator(selectedSupplier, doctorList);
          }
          this.setState({validateLoading: false});
          // 根据valid_doctors更新暂存的医生uuid列表
          if (errorRes) {
            if (Array.isArray(errorRes.valid_doctors)) {
              this.tempSubmitParam.param.doctors = errorRes.valid_doctors;
            }
            if (Array.isArray(errorRes.err_code) && errorRes.err_code.length > 0) {
              const errorList = this.transferErrorList(errorRes.err_code, submitParam.medicine_group);
              this.setState({modalVisible: true, errorList});
            } else {
              // 没有错误信息， 直接调用确认提交接口
              await this.confirmSubmit();
            }
          }
        }catch (error) {
          this.tempSubmitParam = undefined;
          message.error(I18N.get('err_save_failure'));
          console.error(error);
        }
      }
    })
  }

  /**
   * 处理医生关联配药中心逻辑 (目前废弃，后台处理)
   * 配药中心关联判断条件: 1, 新增关联操作; 2, 关联的供应商是配药中心
   * 批量关联时, 先批量关联配药中心;
   * 取消关联时暂不处理配药中心相关关联
   * @param operateType: [关联|解除关联]
   * @param supplier: 选中的供应商[配药中心|代理商]
   * @param validDoctorUuids: 验证通过的医生id数组
   */
  // private batchLinkDrugstore = async (operateType: 'bind' | 'unBind', supplier: ISupplierDict | undefined, validDoctorUuids: string[]): Promise<boolean> => {
  //   let drugstoreLinked = true;
  //   if (operateType === 'bind' && !!supplier && supplier.type === 'drugstore') {
  //     const param = {
  //       doctors: validDoctorUuids,
  //       drug_store_id: supplier.id,
  //     };
  //     drugstoreLinked =  await DoctorService.batchLinkDrugstore(param);
  //   }
  //   return drugstoreLinked;
  // }

  /**
   * 确认提交表单
   */
  confirmSubmit = async() => {
    const {submitLoading} = this.state;
    if (!this.tempSubmitParam) {
      return
    }
    if (submitLoading) {
      return;
    }
    const {operateType, param} = this.tempSubmitParam;
    this.setState({submitLoading: true});
    // 处理单元药库关联逻辑
    const res = await DoctorService.batchModifySupplier(operateType, param);
    if (!!res) {
      message.success(I18N.get('common_save_success'));
      this.setState({submitLoading: false, modalVisible: false});
      this.goback();
    } else {
      message.error(I18N.get('err_save_failure'));
      this.setState({submitLoading: false});
    }
    // 关联配药中心(目前废弃)
    // const drugstoreLinked = await this.batchLinkDrugstore(operateType, selectedSupplier, param.doctors);
    // if (drugstoreLinked) {
    //   // 处理完配药中心关联逻辑后, 再处理单元药库关联逻辑
    //   const res = await DoctorService.batchModifySupplier(operateType, param);
    //   if (!!res) {
    //     message.success(I18N.get('common_save_success'));
    //     this.setState({submitLoading: false, modalVisible: false});
    //     this.goback();
    //   } else {
    //     message.error(I18N.get('err_save_failure'));
    //     this.setState({submitLoading: false});
    //   }
    // }
  }

  /**
   * 关闭确认弹窗
   */
  closeModal = () => {
    this.setState({modalVisible: false});
  }

  handleTableOnChange = (pagination: PaginationProps, filters: any, sorter: any) => {
    const pager: PaginationProps = { ...this.state.pagination };
    pager.current = pagination.current;
    pager.pageSize = pagination.pageSize;
    this.setState({
      pagination: pager,
    });
  };

  /**
   * 过滤出[在线|启用]的配药中心并转化为供应商字典类型
   */
  getSupplierList = () => {
    const {drugStoreList, userInfo, isAgent} = this.props;
    const result: ISupplierDict[] = [];
    if (isArray(drugStoreList)) {
      drugStoreList.forEach(drugStore => {
        if (drugStore.is_online && drugStore.is_active) {
          result.push({
            id: drugStore.id,
            seller_user: drugStore.seller_user,
            name: drugStore.name,
            type: 'drugstore',
            regions: drugStore.regions || [],
          });
        }
      })
    }
    // 添加代理商
    if (isAgent && !!userInfo) {
      const agentDictItem: ISupplierDict = {
        id: userInfo.id,
        seller_user: userInfo.seller_user,
        name: userInfo.name,
        type: 'agent',
        regions: userInfo.regions || [],
      };
      result.unshift(agentDictItem);
    }
    this.setState({supplierList: result});
  }

  render() {
    const {
      searchType,
      provinceList,
      cityList,
      districtList,
      selectedProvince,
      selectedCity,
      selectedDistrict,
      searchLoading,
      submitLoading,
      modalVisible,
      doctorList,
      columns,
      priceModeList,
      unitPharmacyList,
      errorList,
      pagination,
      allPharmacyList,
      supplierList,
      selectedPharmacy,
    } = this.state;
    const {
      form,
    } = this.props;
    const {getFieldDecorator, getFieldValue} = form;
    const formItemLayout = {
      labelCol: { span: 4 },
      wrapperCol: { span: 20 },
    };
    
    return (
      <div>
        <Row type="flex" align="middle" justify="space-between">
          <span style={{color: '#1FB8CD', fontSize: 16}}>{I18N.get('doc_batch_modify')}</span>
          <Button type="primary" onClick={this.goback}>
            {I18N.get('common_back')}
          </Button>
        </Row>
        <Row style={{marginTop: 10}}>
          <Col span={12} style={{paddingRight: 40}}>
            <div style={{fontSize: 16, color: '#222'}}>1.{I18N.get('doc_batch_modify_step1')}</div>

            <Row style={{marginTop: 12}}>
              <Radio.Group onChange={this.handleSearchTypeChange} defaultValue="area" disabled={searchLoading}>
                <Radio.Button value="area">{I18N.get('doc_area_belong')}</Radio.Button>
                <Radio.Button value="all">{I18N.get('common_status_all') + I18N.get('common_doctor')}</Radio.Button>
              </Radio.Group>
            </Row>
            
            {searchType === 'area' && (
              <Row type="flex" align="bottom" justify="space-between" style={{marginTop: 12}}>
                <div className="auto-flex-item">
                  <div className="flex-row">
                    <label
                      style={{marginRight: 10}}
                      className="fixed-flex-item"
                      htmlFor="cascader-wrap"
                    >
                      {I18N.get('doc_area_belong')}:
                    </label>
                    <div id="cascader-wrap" className="auto-flex-item flex-row">
                      <Select
                        className="auto-flex-item"
                        style={{marginRight: 10, maxWidth: 120}}
                        value={!!selectedProvince ? selectedProvince.text : undefined}
                        onChange={this.provinceChange}
                        placeholder={I18N.get('common_choose')}
                        allowClear={true}
                      >
                        {provinceList.map(province => (
                          <Select.Option key={String(province.value)} value={province.value}>
                            {province.text}
                          </Select.Option>
                        ))}
                      </Select>
                      <Select
                        className="auto-flex-item"
                        style={{marginRight: 10, maxWidth: 120}}
                        value={!!selectedCity ? selectedCity.text : undefined}
                        onChange={this.cityChange}
                        placeholder={I18N.get('common_choose')}
                        allowClear={true}
                      >
                        {cityList.map(city => (
                          <Select.Option key={String(city.value)} value={city.value}>
                            {city.text}
                          </Select.Option>
                        ))}
                      </Select>
                      <Select
                        className="auto-flex-item"
                        style={{maxWidth: 120}}
                        value={!!selectedDistrict ? selectedDistrict.text : undefined}
                        onChange={this.districtChange}
                        placeholder={I18N.get('common_choose')}
                        allowClear={true}
                      >
                        {districtList.map(district => (
                          <Select.Option key={String(district.value)} value={district.value}>
                            {district.text}
                          </Select.Option>
                        ))}
                      </Select>
                    </div>
                  </div>
                  <div className="flex-row" style={{marginTop: 10}}>
                    <label
                      style={{marginRight: 10}}
                      className="fixed-flex-item"
                      htmlFor="pharmacy-select"
                    >
                      {I18N.get('unit_pharmacy')}:
                    </label>
                    <SupplierPharmacySelect
                      className="auto-flex-item"
                      style={{maxWidth: 380}}
                      value={selectedPharmacy}
                      pharmacyList={allPharmacyList}
                      showNone={true}
                      onChange={this.onUnitPharmacySelect}
                    />
                  </div>
                </div>
                <Button
                  className="fixed-flex-item"
                  style={{marginLeft: 10}}
                  type="primary"
                  loading={searchLoading}
                  onClick={this.searchDoctors}
                >
                  {I18N.get('common_search_add')}
                </Button>
              </Row>
              
            )}
            {/* {searchType === 'all' && (
              <Row style={{marginTop: 30}}>
                {`${I18N.get('doc_batch_search_all')}`}
              </Row>
            )} */}
            <div style={{marginTop: 30}}>
              <Row gutter={0} type="flex" align="middle" style={{marginBottom: 20}}>
                <span>
                  {`${I18N.get('doc_batch_modify_total_prefix')}${doctorList.length}${I18N.get('doc_batch_modify_total_suffix')}`}
                </span>
                <span
                  style={{marginLeft: 16, color: '#1FB8CD', cursor: 'pointer', userSelect: 'none'}}
                  onClick={this.clearDoctorList}
                >
                  {I18N.get('common_clear')}
                </span>
              </Row>
              <Table
                loading={searchLoading}
                columns={columns}
                rowKey="id"
                dataSource={doctorList}
                pagination={pagination}
                onChange={this.handleTableOnChange}
              />
            </div>
            
          </Col>
          <Col span={12} style={{paddingLeft: 40, borderLeft: '1px solid #E5E5E5'}}>
            <div style={{fontSize: 16, color: '#222'}}>2.{I18N.get('doc_batch_modify_step2')}</div>
            <Form style={{marginTop: 20}}>
              <Form.Item style={{marginBottom: 16}}>
                {getFieldDecorator('operate_type', {
                  initialValue: 'bind',
                })(
                  <Radio.Group onChange={this.handleOperateChange}>
                    <Radio.Button value="bind">{I18N.get('doc_batch_modify_bind')}</Radio.Button>
                    <Radio.Button value="unBind">{I18N.get('doc_batch_modify_unBind')}</Radio.Button>
                  </Radio.Group>,
                )}
              </Form.Item>
              <div style={{
                backgroundColor: '#F9F9F9',
                border: '1px dashed #DDDDDD',
                padding: 16
              }}>
                {/* 只有新增关联才显示配药中心和价格模式  */}
                {getFieldValue('operate_type') === 'bind' && (
                  <Form.Item
                    label={I18N.get('med_supplier')}
                    style={{marginBottom: 10}}
                    {...formItemLayout}
                  >
                    {getFieldDecorator('seller_user', {
                      rules: [{
                          required: true,
                          message: `${I18N.get('common_choose')}`
                      }],
                    })(
                      <Select
                        allowClear={true}
                        placeholder={I18N.get('common_choose')}
                        onChange={this.onChangeDrugStore}
                      >
                        {
                          supplierList.map(d => {
                            return (
                              <Select.Option key={String(d.seller_user)} value={d.seller_user}>
                                {d.name}
                              </Select.Option>
                            );
                          })
                        }
                      </Select>
                    )}
                  </Form.Item>
                )}
                <Form.Item
                  label={I18N.get('unit_pharmacy')}
                  style={{marginBottom: 10}}
                  {...formItemLayout}
                >
                  {getFieldDecorator('medicine_group', {
                    rules: [{
                        required: true,
                        message: `${I18N.get('err_choose_unit_pharmacy')}`
                    }],
                  })(
                    <SupplierPharmacySelect
                        pharmacyList={unitPharmacyList}
                        showNone={false}
                        onChange={this.onChangeUnitPharmacy}
                      />
                  )}
                </Form.Item>
                {getFieldValue('operate_type') === 'bind' && (
                  <Form.Item
                      label={I18N.get('med_price_area')}
                      style={{marginBottom: 10}}
                      {...formItemLayout}
                    >
                      {getFieldDecorator('medicine_price_policy', {
                        rules: [{
                            message: `${I18N.get('err_choose_price_mode')}`
                        }],
                      })(
                        <Select
                          allowClear={true}
                          placeholder={I18N.get('err_choose_price_mode')}
                        >
                          {
                            priceModeList.map(d => {
                              return (
                                <Select.Option key={String(d.uuid)} value={d.uuid}>
                                  {d.name}
                                </Select.Option>
                              );
                            })
                          }
                        </Select>
                      )}
                    </Form.Item>
                )}
              </div>
              <Form.Item style={{textAlign: 'center', margin: '30px 0 0 0'}}>
              <Popconfirm
                onConfirm={this.handleSubmit}
                placement="bottom"
                title={I18N.get('common_submit_confirm')}
                okText={I18N.get('common_ok')}
                cancelText={I18N.get('common_cancel')}
              >
                <Button type="primary">
                  {I18N.get('common_submit')}
                </Button>
              </Popconfirm>,
              </Form.Item>
            </Form>
          </Col>
        </Row>
        <Modal
          centered={true}
          title={I18N.get('common_tips')}
          visible={modalVisible}
          width={600}
          onCancel={this.closeModal}
          destroyOnClose={true}
          footer={null}
        >
          {errorList.map((error, index) => {
            return (<div key={index}>{`${index + 1}.${error}`}</div>);
          })}
          <Row style={{marginTop: 30}} type="flex" align="middle" justify="center">
            <Button
              type="primary"
              loading={submitLoading}
              onClick={this.confirmSubmit}
            >
              {I18N.get('common_submit_ensure')}
            </Button>
            <Button style={{marginLeft: 20}} onClick={this.closeModal}>
              {I18N.get('common_cancel')}
            </Button>
          </Row>
        </Modal>
      </div>
    );
  }
}
const DoctorBatchModifyPage = Form.create<IDoctorBatchModifyFormProps>()(DoctorBatchModifyForm);
export default DoctorBatchModifyPage;



// WEBPACK FOOTER //
// ./src/components/views/Doctor/DoctorBatchModifyPage.tsx